import type { LucideIcon } from "lucide-react";
import * as React from "react";
import type { ReactNode } from "react";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from "@/shared/components/primitives/dropdown-menu";
import { cn } from "@/shared/lib/utils";

export type ColocaDropdownItem = {
  /** Text or node shown as the item label. */
  label: ReactNode;
  /** Leading icon — use to reinforce what the action does, e.g. `Pencil` for edit. */
  icon?: LucideIcon;
  /** Keyboard shortcut hint shown on the trailing edge, e.g. `"⌘K"`. */
  shortcut?: string;
  /**
   * Use `"destructive"` for irreversible actions such as delete or revoke. The item renders in red and should
   * always pair with a confirmation step.
   */
  variant?: "default" | "destructive";
  /** Prevents interaction while keeping the item visible in the list. */
  disabled?: boolean;
  onClick?: () => void;
};

export type ColocaDropdownGroup = {
  /** Optional section heading rendered above the group's items. */
  label?: string;
  items: ColocaDropdownItem[];
};

export interface ColocaDropdownMenuProps {
  /**
   * Element that opens the menu on click — typically a `<ColocaButton>` or icon button. The menu wires the
   * trigger automatically; do not add your own `onClick`.
   */
  trigger: React.ReactElement;
  /** Flat list of actions. Mutually exclusive with `groups`. */
  items?: ColocaDropdownItem[];
  /** Grouped actions with optional section labels, separated by dividers. Mutually exclusive with `items`. */
  groups?: ColocaDropdownGroup[];
  /** Which side of the trigger the menu opens on. Defaults to `"bottom"`. */
  side?: "top" | "right" | "bottom" | "left";
  /** Alignment of the menu panel relative to the trigger edge. Defaults to `"start"`. */
  align?: "start" | "center" | "end";
  /** Gap in px between the trigger and the menu panel. Defaults to `4`. */
  sideOffset?: number;
  /** Extra classes applied to the menu panel — use to override the default min-width. */
  contentClassName?: string;
  /** Controlled open state. Pair with `onOpenChange` to drive the menu from outside. */
  open?: boolean;
  /** Uncontrolled initial open state. */
  defaultOpen?: boolean;
  /** Called when the menu opens or closes. */
  onOpenChange?: (open: boolean) => void;
}

function renderItem(item: ColocaDropdownItem, index: number) {
  const Icon = item.icon;
  return (
    <DropdownMenuItem key={index} variant={item.variant} disabled={item.disabled} onClick={item.onClick}>
      {(Icon && <Icon />) || <div className="size-4"></div>}
      {item.label}
      {item.shortcut && <DropdownMenuShortcut>{item.shortcut}</DropdownMenuShortcut>}
    </DropdownMenuItem>
  );
}

/**
 * Use `ColocaDropdownMenu` for contextual action menus triggered by click — per-row table actions, avatar
 * menus, overflow `"…"` buttons, or any list of mutually exclusive commands.
 *
 * For non-action overlays with rich content use `ColocaPopover`. For destructive confirmations always follow
 * a destructive item with a `ColocaAlertDialog`.
 *
 * @summary Contextual action menu triggered by click — table rows, avatars, overflow buttons.
 */
export function ColocaDropdownMenu({
  trigger,
  items,
  groups,
  side = "bottom",
  align = "start",
  sideOffset = 4,
  contentClassName,
  open,
  defaultOpen,
  onOpenChange,
}: ColocaDropdownMenuProps) {
  const resolvedGroups: ColocaDropdownGroup[] = groups ?? (items ? [{ items }] : []);

  return (
    <DropdownMenu open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <DropdownMenuTrigger render={trigger} />
      <DropdownMenuContent
        side={side}
        align={align}
        sideOffset={sideOffset}
        className={cn("w-max", contentClassName)}
      >
        {resolvedGroups.map((group, gi) => (
          <React.Fragment key={gi}>
            {gi > 0 && <DropdownMenuSeparator />}
            <DropdownMenuGroup>
              {group.label && <DropdownMenuLabel>{group.label}</DropdownMenuLabel>}
              {group.items.map(renderItem)}
            </DropdownMenuGroup>
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
